import * as React from "react";
import styled from "@emotion/styled";
import { Image } from "@shopify/hydrogen-react";
import {
  Box,
  Button,
  FakeLabel,
  FieldError,
  FlexRow,
  H2,
  LoadingEllipses,
  PlaceholderBlock,
  RequiredText,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import {
  acceptTypes,
  HiddenInput,
  parseS3URL,
  useMultipleFileUpload,
} from "features/images";
import {
  useUpdateProductDetails,
  type UpdateProductDetailsReq,
} from "../../api/useUpdateProductDetails";
import { ProductWizardStep } from "../ProductWizardStep";
import type { StepProps } from "../../types";
import { isMediaImage } from "../../utils/isMediaImage";

function StepProductMedia({ dispatch, state, moveBack }: StepProps) {
  const { isLoading, isError, error, mutate } = useUpdateProductDetails(
    state.product.id,
  );

  const uploadMutation = useMultipleFileUpload();

  // Create ref to focus the input button on failed validation and state to store an error message
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = React.useState<string>();

  // Create a single helper function to mutate the product with all required default values
  const update = (updates: Partial<UpdateProductDetailsReq>) =>
    mutate({
      mediaURLsToAdd: [],
      mediaIDsToDelete: [],
      mediaIDsToSequence: [],
      ...updates,
    });

  // Create a delete handler for each media given their ID
  const createDeleteHandler = (id: string) => () =>
    update({ mediaIDsToDelete: [id] });

  const moveNext = () => {
    // If this template has validation for this step, run it
    const validate = state.productTemplate.validate.productMedia;
    if (validate) {
      const result = validate(state.product.media);

      if (
        Array.isArray(result) ||
        typeof result === "string" ||
        result === false
      ) {
        // If validation failed, show the error and exit early
        setValidationError(
          (Array.isArray(result) ? result[0] : result) ||
            "This step is required.",
        );
        inputRef.current?.focus();
        return;
      }
    }
    // If validation passed, or there is none, move to next step
    dispatch({ type: "moveNext" });
  };

  const addFiles: React.ChangeEventHandler<HTMLInputElement> = async ({
    currentTarget: { files },
  }) => {
    if (files) {
      uploadMutation.mutate([...files], {
        onSuccess: (resp) => {
          // We've successfully updloaded media so update the Product with their urls
          update({
            mediaURLsToAdd: resp.map(({ url }) => parseS3URL(url)),
          });
        },
      });
    }
  };

  const [featuredMedia, ...additionalMedia] =
    state.product.media.filter(isMediaImage);
  const hasMedia = state.product.media.length > 0;
  const hasAdditionalMedia = additionalMedia.length > 0;

  return (
    <ProductWizardStep
      stepTitle="Product Media"
      state={state}
      moveBack={moveBack}
      nextStep={moveNext}
      nextStepDisabled={isLoading || uploadMutation.isLoading}
    >
      <Stack gap="md">
        {featuredMedia?.image && (
          <Stack gap="xs">
            <FakeLabel as={Stack}>
              <Text size="sm" bold>
                Featured Image
                <RequiredText />
              </Text>
            </FakeLabel>

            <Text size="xs">
              The main product image that will be displayed on the product card
              and on the product page.
            </Text>
            <AdditionalMedia border>
              <Image
                data={featuredMedia.image}
                aspectRatio="1"
                sizes="(min-width: 45em) 40vw, 100vw"
              />
              <Button
                variant="blank"
                onClick={createDeleteHandler(featuredMedia.id)}
                disabled={isLoading}
              >
                Delete
              </Button>
            </AdditionalMedia>
          </Stack>
        )}

        {hasAdditionalMedia && (
          <Stack gap="xs">
            <FakeLabel as={Stack}>
              <Text size="sm" bold>
                Additional Product Images
              </Text>
            </FakeLabel>

            <AdditionalMediaGrid>
              {additionalMedia.map((data) => (
                <div key={data.id}>
                  <Image data={data?.image} />

                  <Button
                    variant="blank"
                    onClick={createDeleteHandler(data.id)}
                    disabled={isLoading}
                  >
                    Delete
                  </Button>
                </div>
              ))}
              {uploadMutation.isLoading &&
                uploadMutation.variables?.map((file) => (
                  <PlaceholderBlock
                    key={file.name}
                    width="2rem"
                    height="2rem"
                  />
                ))}
            </AdditionalMediaGrid>
          </Stack>
        )}

        <Stack gap="xs">
          <H2 textTransform="none" size="md">
            Add <span>{hasMedia ? "More" : "Product"}</span> Product Images
          </H2>
          <Text size="xxs">
            Supports {acceptTypes.image.labels.join(", ")} files. Animated GIFs
            not currently supported. 10MB max file size.
          </Text>

          <FlexRow>
            <Button as="label" variant="secondary">
              Upload From Device
              <HiddenInput
                type="file"
                onChange={addFiles}
                accept={acceptTypes.image.values}
                multiple
                ref={inputRef}
              />
            </Button>
          </FlexRow>
          {validationError && <FieldError>{validationError}</FieldError>}

          {uploadMutation.isLoading && (
            <p>
              uploading files <LoadingEllipses />
            </p>
          )}
          {uploadMutation.isError && (
            <UnexpectedError error={uploadMutation.error} />
          )}
        </Stack>

        {isError && <UnexpectedError error={error} />}
      </Stack>
    </ProductWizardStep>
  );
}

StepProductMedia.displayName = "Images";

const AdditionalMediaGrid = styled.div(({ theme }) => ({
  display: "grid",
  gap: theme.spacing.sm,
  gridTemplateColumns: "repeat(2, 1fr)",
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
}));

const AdditionalMedia = styled(Box)(({ theme }) => ({
  padding: "1rem",
  overflow: "hidden",
  display: "grid",
  justifyContent: "center",
  [theme.breakpoints.tablet]: { padding: "1rem" },
  [theme.breakpoints.desktop]: { padding: "1rem" },
}));

export { StepProductMedia };
